import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime, formatDate } from "@/lib/format";
import { Segmented, InfoTip } from "@/components/settings/Grouped";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Plug, Copy, Trash2, Check, Sparkles, Activity, CheckCircle2, XCircle, Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Asistenti, se kterými konektor funguje (168). Volba mění jen návod a předvyplněný název.
const ASSISTANTS = {
  claude: {
    label: "Claude",
    steps: [
      <>V <b>Claude</b> otevřete <b>Nastavení → Konektory → Přidat vlastní konektor</b>.</>,
      <>Vložte URL níže a uložte.</>,
      <>V chatu konektor zapněte přes „+".</>,
    ],
  },
  chatgpt: {
    label: "ChatGPT",
    // Cesta ověřená v ChatGPT (09/2026): Pluginy → Přidat → Vytvořit aplikaci MCP.
    steps: [
      <>V <b>ChatGPT na webu</b> otevřete vlevo <b>Pluginy</b> a vpravo nahoře <b>Přidat → Vytvořit aplikaci MCP</b>.</>,
      <>Název: <b>Proyekty</b>. U <b>Připojení</b> nechte <b>Adresa URL serveru</b> a vložte URL níže.</>,
      <><b>Ověřování přepněte z „OAuth“ na „Žádné“</b> — přístup je schovaný v URL. S OAuth se připojení nepovede.</>,
      <>Zaškrtněte <b>Rozumím a chci pokračovat</b> a dejte <b>Vytvořit</b>. V chatu pak Proyekty zapnete přes „+". Před každým zápisem (úkol, deník, docházka) se ChatGPT zeptá na potvrzení.</>,
    ],
    note: "Pokud v Pluginech volbu „Vytvořit aplikaci MCP“ nevidíte, váš tarif ChatGPT ji nemá; u firemního účtu ji musí povolit správce. URL je jako heslo: kdo ji zná, pracuje v Proyekty pod vaším jménem — nesdílejte ji a při podezření ji níže zneplatněte.",
  },
};

export function McpConnector() {
  const [assistant, setAssistant] = useState("claude");
  const A = ASSISTANTS[assistant];
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState(A.label);
  const [scope, setScope] = useState("read_write");   // 322: jen čtení / čtení i zápis
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null); // { url, label } shown once
  const [copied, setCopied] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testUrl, setTestUrl] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, msg }
  const [revokeT, setRevokeT] = useState(null);

  const load = () => api.get("/mcp-tokens").then((r) => setTokens(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pick = (key) => {
    // Název sleduje zvoleného asistenta, dokud ho uživatel nepřepsal po svém.
    setName((n) => (Object.values(ASSISTANTS).some((x) => x.label === n) || !n.trim() ? ASSISTANTS[key].label : n));
    setAssistant(key);
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mcp-tokens", { name: name.trim() || A.label, scope });
      // URL vydává backend (MCP_PUBLIC_BASE_URL = doména Workeru). Neskládat ji tady
      // z BACKEND_URL — na hlavní doméně konektor v Claude nefunguje.
      const url = data.url || `${BACKEND_URL}/api/mcp/${data.token}`;
      setFresh({ url, label: A.label });
      setTestUrl(url); setTestResult(null);
      setName(A.label);
      toast.success("URL vytvořena. Zkopírujte ji — zobrazí se jen teď.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    const t = revokeT;
    setRevokeT(null);
    try {
      await api.delete(`/mcp-tokens/${t.id}`);
      toast.success("Propojení zneplatněno."); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = () => {
    navigator.clipboard.writeText(fresh.url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  // Ověření konektoru = přesně to, co udělá asistent při přidání: JSON-RPC initialize.
  // Záměrně přes fetch, ne přes `api` — axios interceptor by případnou 401 (starší
  // backend) vyhodnotil jako expirované přihlášení a odhlásil uživatele.
  // Z vloženého textu bereme jen token a posíláme ho vždy na vlastní backend,
  // takže „test" nikdy neodejde na cizí doménu, ať uživatel vloží cokoliv.
  const rpc = (token, method, params) =>
    fetch(`${BACKEND_URL}/api/mcp/${token}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params: params || {} }),
    });

  const testConnection = async () => {
    const token = (testUrl.match(/pk_[A-Za-z0-9_-]+/) || [])[0];
    if (!token) {
      setTestResult({ ok: false, msg: "V odkazu chybí token konektoru (má začínat „pk_“). Vložte celou URL." });
      return;
    }
    setTesting(true); setTestResult(null);
    try {
      const res = await rpc(token, "initialize", { protocolVersion: "2025-06-18", capabilities: {}, clientInfo: { name: "Proyekty – test", version: "1.0" } });
      const data = await res.json().catch(() => null);
      if (data?.error) { setTestResult({ ok: false, msg: data.error.message }); return; }
      if (!res.ok || !data?.result) {
        setTestResult({ ok: false, msg: data?.error?.message || data?.error || `Server odpověděl chybou (HTTP ${res.status}).` });
        return;
      }
      const proto = data.result.protocolVersion;
      // Handshake prošel; pro srozumitelné potvrzení doplníme, pod kým asistent uvidí data.
      let who = null;
      try {
        const r2 = await rpc(token, "tools/call", { name: "whoami", arguments: {} });
        const d2 = await r2.json();
        who = JSON.parse(d2.result.content[0].text);
      } catch { /* detail je bonus — hlavní je, že handshake prošel */ }
      setTestResult({ ok: true, msg: (who
        ? `Spojení funguje. Asistent uvidí data jako ${who.name} (${who.role}), firma ${who.company}.`
        : "Spojení funguje. URL je možné přidat do asistenta.") + (proto ? ` · protokol ${proto}` : "") });
    } catch {
      setTestResult({ ok: false, msg: "Server neodpověděl. Zkontrolujte připojení a zkuste to znovu." });
    } finally { setTesting(false); }
  };

  return (
    <div className="bg-card border border-border rounded-[20px] p-5 space-y-4" data-testid="mcp-connector-section">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-brand/10 text-brand grid place-items-center shrink-0"><Sparkles size={18} /></div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold leading-tight">Propojení s AI asistentem</h2>
          <p className="text-sm text-muted-foreground">Ptejte se na ekonomiku, docházku a sklad, nechte zapsat deník a úkoly — v Claude nebo v ChatGPT.</p>
        </div>
      </div>

      {/* 1. Asistent — segmented uvnitř karty (kánon: šedý kontejner, bílá aktivní) */}
      <div className="flex p-1 rounded-full bg-secondary w-full md:w-fit" role="tablist" data-testid="mcp-assistant-toggle">
        {Object.entries(ASSISTANTS).map(([key, a]) => (
          <button key={key} type="button" role="tab" aria-selected={assistant === key} onClick={() => pick(key)}
            data-testid={`mcp-assistant-${key}`}
            className={`flex-1 md:flex-none h-9 px-5 rounded-full text-sm font-bold transition-colors motion-reduce:transition-none ${
              assistant === key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
            {a.label}
          </button>
        ))}
      </div>

      {/* 2. Jak to zapojit */}
      <div className="text-sm text-muted-foreground space-y-1.5 bg-secondary/40 rounded-[14px] p-3" data-testid="mcp-howto">
        <p className="font-semibold text-foreground">Jak to zapojit v {A.label}:</p>
        {A.steps.map((st, i) => <p key={i}>{i + 1}. {st}</p>)}
        {A.note && <p className="text-xs">{A.note}</p>}
        <p className="flex items-start gap-1.5 pt-1 text-amber-700 dark:text-amber-400 font-semibold" data-testid="mcp-share-warning">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <span>Každý člověk a každý asistent má mít vlastní URL. Nepřidávejte ji jako firemní / sdílený konektor — všichni by pak pracovali pod vaším jménem a s vaší rolí.</span>
        </p>
      </div>

      {/* 3. Vytvořit — 322: rozsah přístupu (jen čtení nenabídne asistentovi žádný zápis) */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid="mcp-scope-row">
        <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">Asistent smí</span>
        <InfoTip label="Rozsah přístupu" text="Jen čtení: asistent odpovídá na otázky (ekonomika, docházka, sklad, deník), ale nic nezapíše ani nesmaže. Čtení i zápis: zapíše deník, úkoly a docházku; mazání vždy nejdřív ukáže náhled a provede se až po vašem souhlasu. URL platí rok, pak vytvoříte novou." />
        </div>
        <span className="flex-1 hidden sm:block" />
        <div className="w-full sm:w-auto [&>div]:w-full">
          <Segmented value={scope} onChange={setScope} testid="mcp-scope"
            options={[{ value: "read_write", label: "Číst i zapisovat", testid: "mcp-scope-rw" }, { value: "read", label: "Jen číst", testid: "mcp-scope-read" }]} />
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2">
        <div className="flex-1 min-w-0"><Label className="text-xs">Název propojení</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 mt-1 w-full min-w-0" data-testid="mcp-name-input" /></div>
        <Button onClick={create} disabled={busy} className="h-11 font-bold md:self-end w-full md:w-auto" data-testid="mcp-create-btn">
          <Plug size={16} /> Vytvořit URL pro {A.label}
        </Button>
      </div>

      {/* 4. Čerstvá URL */}
      {fresh && (
        <div className="rounded-[14px] border border-brand/40 bg-brand/5 p-3 space-y-2" data-testid="mcp-fresh-token">
          <div className="text-xs font-bold uppercase tracking-wide text-brand flex items-center gap-1"><Plug size={13} /> URL pro {fresh.label} (zobrazí se jen jednou)</div>
          <div className="flex gap-2">
            <Input readOnly value={fresh.url} className="h-11 font-mono text-xs min-w-0" data-testid="mcp-token-url" onFocus={(e) => e.target.select()} />
            <Button onClick={copy} variant="outline" className="h-11 shrink-0" aria-label="Kopírovat" data-testid="mcp-copy-btn">{copied ? <Check size={16} /> : <Copy size={16} />}</Button>
          </div>
          <p className="text-xs text-muted-foreground">URL je heslo — neposílejte ji dál. Zneplatnit ji můžete níže.</p>
        </div>
      )}

      {/* 5. Moje propojení */}
      {tokens.length > 0 && (
        <div className="border-t border-border pt-3" data-testid="mcp-token-list">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Moje propojení</div>
          <div className="divide-y divide-border">
            {tokens.map((t) => (
              <div key={t.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`mcp-token-${t.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{t.name}
                    {t.scope === "read" && <span className="ml-2 align-middle rounded-full bg-secondary px-2 py-0.5 text-[11px] font-bold text-muted-foreground">jen čtení</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Vytvořeno {formatDateTime(t.created_at)}{t.expires_at ? ` · platí do ${formatDate(t.expires_at)}` : ""}
                    {t.last_used_at ? ` · naposledy použito ${formatDateTime(t.last_used_at)}${t.last_client ? ` z ${t.last_client}` : ""}` : " · zatím nepoužito"}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setRevokeT(t)} aria-label="Zneplatnit"
                  className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`mcp-revoke-${t.id}`}><Trash2 size={16} /></Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. Otestovat — podpůrný nástroj, schovaný */}
      <div className="border-t border-border pt-3 space-y-2" data-testid="mcp-test-section">
        <button type="button" onClick={() => setTestOpen((o) => !o)} aria-expanded={testOpen}
          className="text-sm font-bold text-brand hover:underline inline-flex items-center gap-1 min-h-[40px]" data-testid="mcp-test-toggle">
          Otestovat URL… <ChevronDown size={15} className={`transition-transform motion-reduce:transition-none ${testOpen ? "rotate-180" : ""}`} />
        </button>
        {testOpen && (
          <>
            <p className="text-xs text-muted-foreground">
              Vložte URL, kterou máte v asistentovi, a ověřte, že funguje — dřív, než ji budete řešit s klientem.
            </p>
            <div className="flex flex-col md:flex-row gap-2">
              <Input value={testUrl} onChange={(e) => { setTestUrl(e.target.value); setTestResult(null); }}
                placeholder={`${BACKEND_URL}/api/mcp/pk_…`} className="h-11 font-mono text-xs w-full min-w-0" data-testid="mcp-test-input" />
              <Button onClick={testConnection} disabled={testing || !testUrl.trim()} variant="outline"
                className="h-11 font-bold shrink-0 w-full md:w-auto" data-testid="mcp-test-btn">
                {testing ? <Loader2 size={16} className="animate-spin motion-reduce:animate-none" /> : <Activity size={16} />} Otestovat
              </Button>
            </div>
            {testResult && (
              <div data-testid="mcp-test-result"
                className={`flex items-start gap-2 text-sm rounded-[14px] p-3 ${testResult.ok ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                {testResult.ok ? <CheckCircle2 size={16} className="shrink-0 mt-0.5" /> : <XCircle size={16} className="shrink-0 mt-0.5" />}
                <span className="min-w-0 break-words">{testResult.msg}</span>
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog open={!!revokeT} onOpenChange={(o) => { if (!o) setRevokeT(null); }}>
        <AlertDialogContent data-testid="mcp-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Zneplatnit {revokeT?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeT?.last_client || "Asistent"} ztratí přístup okamžitě. Ostatní propojení zůstanou funkční.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setRevokeT(null)} className="h-11 w-full sm:w-auto">Zpět</Button>
            <Button variant="destructive" onClick={revoke} className="h-11 w-full sm:w-auto font-bold" data-testid="mcp-revoke-confirm">Zneplatnit</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
